<template>
  <Teleport to="body">
    <div
      v-if="open"
      ref="overlay"
      class="lightbox"
      role="dialog"
      aria-modal="true"
      :aria-label="label"
      @click.self="close"
      @keydown.esc.prevent="close"
      @keydown.left="hasPaging && $emit('prev')"
      @keydown.right="hasPaging && $emit('next')"
      @keydown.tab="trapFocus"
    >
      <button ref="closeButton" class="lightbox__close" type="button" :aria-label="closeLabel" @click="close">
        <span aria-hidden="true">&times;</span>
      </button>

      <button
        v-if="hasPaging"
        class="lightbox__nav lightbox__nav--prev"
        type="button"
        :aria-label="prevLabel"
        @click="$emit('prev')"
      >
        <span aria-hidden="true">&#8249;</span>
      </button>

      <figure class="lightbox__figure">
        <img class="lightbox__img" :src="src" :alt="alt" />
        <figcaption v-if="caption || hasPaging" class="lightbox__caption">
          <span v-if="caption">{{ caption }}</span>
          <span v-if="hasPaging" class="lightbox__counter">{{ index + 1 }} / {{ total }}</span>
        </figcaption>
      </figure>

      <button
        v-if="hasPaging"
        class="lightbox__nav lightbox__nav--next"
        type="button"
        :aria-label="nextLabel"
        @click="$emit('next')"
      >
        <span aria-hidden="true">&#8250;</span>
      </button>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';

/*
 * Replaces ZoomModal, which was a plain div with a click handler: no role,
 * no aria-modal, no Escape, no focus trap, no close button, and an image
 * rendered as a CSS background so it had no accessible name at all.
 *
 * The body scroll-lock also lived in the *calling view* and was only undone
 * by its close handler, so navigating away while zoomed left the entire app
 * unscrollable. It is owned here and released on unmount.
 */
const props = defineProps({
  open: Boolean,
  src: { type: String, default: '' },
  alt: { type: String, default: '' },
  caption: { type: String, default: '' },
  label: { type: String, default: 'Image viewer' },
  closeLabel: { type: String, default: 'Close' },
  /* Paging is optional; a single-image lightbox renders no arrows. */
  index: { type: Number, default: 0 },
  total: { type: Number, default: 0 },
  prevLabel: { type: String, default: 'Previous image' },
  nextLabel: { type: String, default: 'Next image' },
});

const emit = defineEmits(['close', 'prev', 'next']);

const hasPaging = computed(() => props.total > 1);

const overlay = ref(null);
const closeButton = ref(null);
let lastFocused = null;

const close = () => emit('close');

function trapFocus(event) {
  const focusable = overlay.value?.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  if (!focusable?.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function lock() {
  lastFocused = document.activeElement;
  document.body.style.overflow = 'hidden';
  nextTick(() => closeButton.value?.focus());
}

function release() {
  document.body.style.overflow = '';
  if (lastFocused instanceof HTMLElement) lastFocused.focus();
  lastFocused = null;
}

watch(
  () => props.open,
  (isOpen) => (isOpen ? lock() : release())
);

onBeforeUnmount(() => {
  document.body.style.overflow = '';
});
</script>

<style scoped>
.lightbox {
  position: fixed;
  inset: 0;
  z-index: var(--z-overlay);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--space-6);
  background: rgba(4, 6, 9, 0.92);
  backdrop-filter: blur(6px);
}

.lightbox__close {
  position: absolute;
  top: var(--space-4);
  right: var(--space-4);
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.75rem;
  line-height: 1;
  color: var(--text);
  background: var(--surface-3);
  border: 1px solid var(--border);
  border-radius: var(--radius);
  cursor: pointer;
}

.lightbox__close:hover {
  color: var(--accent);
  border-color: var(--border-accent);
}

.lightbox__figure {
  margin: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-3);
  max-width: 100%;
  max-height: 100%;
}

.lightbox__img {
  max-width: 100%;
  max-height: 80dvh;
  object-fit: contain;
  border-radius: var(--radius);
}

.lightbox__caption {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-1);
  font-size: var(--text-sm);
  color: var(--text-muted);
  text-align: center;
  max-width: var(--measure-narrow);
}

.lightbox__counter {
  font-family: var(--font-mono);
  font-size: var(--text-xs);
  letter-spacing: var(--tracking-label);
  color: var(--text-faint);
}

.lightbox__nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  line-height: 1;
  color: var(--text);
  background: var(--surface-3);
  border: 1px solid var(--border);
  border-radius: var(--radius-pill);
  cursor: pointer;
}

.lightbox__nav:hover {
  color: var(--accent);
  border-color: var(--border-accent);
}

.lightbox__nav--prev { left: var(--space-4); }
.lightbox__nav--next { right: var(--space-4); }

@media (max-width: 600px) {
  .lightbox__nav {
    top: auto;
    bottom: var(--space-4);
    transform: none;
  }
}
</style>
